import { UserSessionContext } from '../../../extViewmodels';
import { AppState } from '../../models/AppState';
import { IPhoneCallRemoteResourceEvent } from '../../viewModels/phonecall';
import { IWebsocketSubscription } from '../../viewModels/websockets';
import { CallEventBus } from './events';

const SUBSCRIPTION_NAME = 'focused-view-phonecall';

/**
 * Subscribes to PhoneCallEvent messages flowing through the global
 * AppState.websockets listener. These events represent phone-call activity
 * initiated or received by *other* clients associated with the user (e.g.
 * a mobile softphone). The browser-originated Twilio SDK path is handled in
 * TwilioDeviceController; this bridge just lets us react when the same user
 * answers a call on a different device.
 *
 * In v1 we only react to hangup events (so the UI returns to idle even if the
 * user ended the call on their phone). Other event types are observed but not
 * bridged — expand as needs arise.
 */
export function subscribeToRemotePhoneCallEvents({
	bus,
	session,
	getActivePhoneCallId,
}: {
	bus: CallEventBus;
	session: UserSessionContext;
	getActivePhoneCallId: () => string | undefined;
}): () => void {
	const websockets = AppState.websockets;
	if (!websockets || !session?.user) return () => {};

	const subscription: IWebsocketSubscription<IPhoneCallRemoteResourceEvent> = {
		name: SUBSCRIPTION_NAME,
		events: ['PhoneCallEvent'],
		callback: events => {
			for (const event of events) {
				const { clientType, hangup, id } = event.value;
				// Ignore events originating from this (web) client — those are
				// already handled by Twilio SDK listeners.
				if (clientType === 'Web') continue;
				if (!hangup) continue;

				// Match by phone-call id, as the legacy telephony context did: the
				// server republishes the whole document on later touches (a
				// transcription finishing, a teammate commenting), so `hangup` stays
				// set on long-finished calls — and `clientType` is only present when
				// it is `Web`, so it can't filter them out.
				const activePhoneCallId = getActivePhoneCallId();
				if (!activePhoneCallId || activePhoneCallId !== id) continue;

				// Server-driven end: the API already recorded it, so this only labels the
				// event for the FSM rather than sending another diagnostic report.
				bus.emit('call:ended', { reason: 'disconnect', cause: 'websocket-hangup' });
			}
		},
	};

	websockets.subscribe(subscription);

	return () => {
		websockets.unsubscribe(SUBSCRIPTION_NAME);
	};
}
